<template>
  <table class="t-table">
    <thead>
      <tr>
        <td width="20%">{{ $t('table.columns.rank') }}</td>
        <td width="30%">{{ $t('table.columns.playerName') }}</td>
        <td width="25%">{{ $t('table.columns.score') }}</td>
        <td width="25%" class="t-table-data">
          <div>
            {{ $t('table.columns.totalWin') }}
            <t-tooltip class="tooltip" :text="$t('table.info.totalWin')">
              <i class="icf-info" />
            </t-tooltip>
          </div>
        </td>
      </tr>
    </thead>
    <tbody data-qe-id="table-data">
      <template v-if="data.length > 0">
        <!-- qualifieds list view -->
        <template v-for="user in data" :key="user.playerName">
          <tr
            v-if="listEndPosition !== lastQualifiedRank && listEndPosition === parseInt(user.seasonRank)"
            class="qualified-list-end-row"
            :key="`${user.playerName}_last_row_before`"
            data-qe-id="qualified-list-end-row"
          >
            <td :colspan="COLSPAN_NUM">{{ $t('table.more.list_end') }}</td>
          </tr>
          <c-table-row
            :highlighted="isMyUser(user)"
            :ranking="user"
            :type="ROW_TYPE"
            :class="isUserQualified(user) ? 'qualified' : false"
          />
        </template>
        <tr
          v-if="(lastQualifiedRank > data.length && !isSearchActive) || listEndPosition === lastQualifiedRank"
          class="qualified-list-end-row"
          data-qe-id="qualified-list-end-row"
        >
          <td :colspan="COLSPAN_NUM">{{ $t('table.more.list_end') }}</td>
        </tr>
        <!-- "show more entries" button/row -->
        <template v-if="maxRows > data.length">
          <tr>
            <td class="dots" :colspan="COLSPAN_NUM">• • •</td>
          </tr>
          <tr>
            <td class="show-more-row" :colspan="COLSPAN_NUM" @click="onShowMore" data-qe-id="show-more-link">
              {{ $t('table.more.button') }} <i class="icf-arrowBottom"></i>
            </td>
          </tr>
        </template>
        <c-table-row
          v-if="!isSearchActive && !isUserQualified(myRank) && !isUserInViewPort(myRank) && !isTicketUser(myRank)"
          :highlighted="true"
          :ranking="myRank"
          :type="ROW_TYPE"
        />
      </template>
      <template v-else>
        <tr class="no-results" data-qe-id="no-results-available-row">
          <td :colspan="COLSPAN_NUM">{{ $t('table.more.no_results') }}</td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import CTableRow from '@/components/ranking/season/CTableRow.vue'
import { SeasonRankingUI } from '@/model/ranking/Season'
import { TableFilter } from '@/model/ranking/TableFilter'

const props = defineProps<{
  data: Array<SeasonRankingUI>
  isSearchActive: boolean
  lastQualifiedRank: number
  maxRows: number
  myRank: SeasonRankingUI
}>()
const emit = defineEmits(['show-more'])

const COLSPAN_NUM = 4
const ROW_TYPE: TableFilter = TableFilter.SCORES

const listEndPosition = ref(0)

watch(
  () => props.data,
  (newData: Array<SeasonRankingUI>, oldData: Array<SeasonRankingUI>) => {
    if (newData.length > 1) {
      // If data contains more than one item set marker for end of qualified-list to rank of first unqualified user
      const qualifieds = newData.filter((ranking) => !isUserQualified(ranking))
      if (qualifieds.length > 0) {
        // first user which does not qualify
        listEndPosition.value = parseInt(qualifieds.shift().seasonRank)
      } else {
        listEndPosition.value = props.lastQualifiedRank
      }
    } else if (newData.length === 1) {
      // If data contains just one item check whether they are qualified and set marker accordingly
      if (isUserQualified(newData[0])) {
        listEndPosition.value = props.lastQualifiedRank
      } else {
        listEndPosition.value = parseInt(newData[0].seasonRank)
      }
    }
  },
  { immediate: true }
)

function isMyUser(user: SeasonRankingUI) {
  return user.playerId === props.myRank.playerId
}

function isUserQualified(user: SeasonRankingUI): boolean {
  return parseInt(user.seasonRank) <= props.lastQualifiedRank
}

function isUserInViewPort(user: SeasonRankingUI): boolean {
  return props.data.filter((ranking) => ranking.playerId === user.playerId).length > 0
}

function isTicketUser(user: SeasonRankingUI): boolean {
  return !!user.ticketInfo
}

function onShowMore() {
  emit('show-more')
}
</script>
